// src/pages/NewCustomerModal.jsx
import { useState } from 'react';
import { toast } from 'react-toastify';
import API from '../api';

export default function NewCustomerModal({ onClose, onCustomerCreated }) {
  const [form, setForm] = useState({
    customer_number: '',
    company_name: '',
    address_for_field_engineer: '',
    onsite_contact_name: '',
    onsite_contact_phone: '',
    onsite_contact_email: '',
    regular_business_hours: '',
    emergency_business_hours: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({...errors, [e.target.name]: ''});
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Validate Customer Number
    if (!form.customer_number) {
      newErrors.customer_number = 'Customer number is required';
      isValid = false;
    } else if (!/^\d+$/.test(form.customer_number)) {
      newErrors.customer_number = 'Customer number must contain only numbers';
      isValid = false;
    }

    // Validate Company Name
    if (!form.company_name) {
      newErrors.company_name = 'Company name is required';
      isValid = false;
    } else if (form.company_name.length < 2) {
      newErrors.company_name = 'Company name must be at least 2 characters';
      isValid = false;
    }

    // Validate Address
    if (!form.address_for_field_engineer) {
      newErrors.address_for_field_engineer = 'Address is required';
      isValid = false;
    } else if (form.address_for_field_engineer.length < 10) {
      newErrors.address_for_field_engineer = 'Please provide a more detailed address';
      isValid = false;
    }

    // Validate Phone Number
    if (form.onsite_contact_phone && !/^\d{10}$/.test(form.onsite_contact_phone)) {
      newErrors.onsite_contact_phone = 'Phone number must be 10 digits (include area code)';
      isValid = false;
    }

    // Validate Email
    if (form.onsite_contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.onsite_contact_email)) {
      newErrors.onsite_contact_email = 'Please enter a valid email address';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const payload = {
        ...form,
        customer_number: String(form.customer_number)
      };

      const response = await API.post('/customers', payload);
      
      toast.success('Customer added successfully!');
      onCustomerCreated();
      onClose();
    } catch (err) {
      console.error("Error adding customer:", err);
      
      const errorMessage = err.response?.data?.message || 
                         err.response?.data?.error || 
                         err.message || 
                         'Failed to add customer';
      
      toast.error(`Error: ${errorMessage}`);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg relative">
        <button onClick={onClose} className="absolute top-2 right-2 text-gray-600 hover:text-red-600 font-bold">&times;</button>
        <h2 className="text-2xl font-bold mb-4 text-blue-600">New Customer</h2>
        <form onSubmit={handleSubmit} className="space-y-3 max-h-[75vh] overflow-y-auto">
          {Object.keys(form).map(field => (
            <div key={field}>
              <label className="block text-sm font-medium capitalize text-gray-700 mb-1" htmlFor={field}>
                {field.replace(/_/g, ' ')}
                {['customer_number', 'company_name', 'address_for_field_engineer'].includes(field) && (
                  <span className="text-red-500"> *</span>
                )}
              </label>
              {field === 'notes' ? (
                <textarea
                  id={field}
                  name={field}
                  value={form[field]}
                  onChange={handleChange}
                  rows="4"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors[field] ? 'border-red-500 focus:ring-red-400' : 'border-gray-300 focus:ring-blue-400'
                  }`}
                  placeholder="Add any additional notes or comments here..."
                />
              ) : (
                <input
                  id={field}
                  name={field}
                  value={form[field]}
                  onChange={handleChange}
                  type={
                    field === 'customer_number' || field === 'onsite_contact_phone' ? 'tel' :
                    field === 'onsite_contact_email' ? 'email' :
                    'text'
                  }
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors[field] ? 'border-red-500 focus:ring-red-400' : 'border-gray-300 focus:ring-blue-400'
                  }`}
                  placeholder={
                    field === 'onsite_contact_phone' ? '10-digit phone number with area code' :
                    field === 'onsite_contact_email' ? 'email@example.com' :
                    field === 'customer_number' ? 'Numbers only' : ''
                  }
                />
              )}
              {errors[field] && (
                <p className="mt-1 text-sm text-red-600">{errors[field]}</p>
              )}
            </div>
          ))}
          <button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 rounded-lg transition"
          >
            Save Customer
          </button>
        </form>
      </div>
    </div>
  );
}